import React from 'react';
import { X, ExternalLink } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetTitle } from '../../components/ui/sheet';
import ManagedNote from './ManagedNote';

const fmt$ = (n) => n ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—';

// Slide-over shell shared by every Board flavor: header, amount + stage banner,
// flavor-supplied body (children), stage picker (one select, ticket 76), optional "open full record".
export default function CardDrawer({
  eyebrow, title, amountLabel, amount,
  columns, currentStage, onChangeStage, onClose,
  openFull, // { label, onClick } | null — null means drawer-only (no detail page)
  stageLock = null, // { text, href, link } | null — the stage is owned by GHL / Arive: read-only, with the link out
  children,
}) {
  const stageInfo = columns.find(c => c.key === currentStage);

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        showClose={false}
        className="p-0 w-full max-w-md sm:max-w-md border-0 shadow-2xl overflow-y-auto bg-white"
        data-testid="pipeline-card-drawer"
      >
        <div className="px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>{eyebrow}</div>
            <SheetTitle className="font-serif text-lg font-bold" style={{ color: '#070B5F' }} data-testid="pipeline-card-drawer-title">{title}</SheetTitle>
          </div>
          <button onClick={onClose} data-testid="pipeline-card-drawer-close" className="p-1 hover:bg-slate-100 rounded-md">
            <X size={16} className="text-slate-500" />
          </button>
        </div>

        <div className="px-6 py-4" style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
          <div className="text-[10px] uppercase tracking-widest text-white/60">{amountLabel}</div>
          <div className="font-serif text-3xl font-bold" style={{ color: '#A6E56C' }}>{fmt$(amount)}</div>
          {stageInfo && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold"
              style={{ background: stageInfo.color + '30', color: '#fff' }} data-testid="pipeline-card-drawer-stage">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: stageInfo.color }} />
              {stageInfo.label}
            </div>
          )}
        </div>

        <div className="p-6 space-y-4">
          {children}

          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Change Stage</div>
            {stageLock && <div className="mb-2"><ManagedNote note={stageLock} testId="drawer-stage-managed" /></div>}
            <Select disabled={!!stageLock} value={currentStage} onValueChange={(v) => { if (v && v !== currentStage) onChangeStage(v); }}>
              <SelectTrigger className="h-9" data-testid="drawer-stage-select" aria-label="Change stage">
                <SelectValue placeholder="Pick a stage…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {columns.map((s) => (
                  <SelectItem key={s.key} value={s.key} data-testid={`drawer-stage-${s.key}`}>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} />
                      {s.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {openFull && (
            <button
              onClick={openFull.onClick}
              data-testid="pipeline-card-drawer-open-full"
              className="w-full mt-2 text-[13px] font-semibold px-4 py-2 rounded-lg text-white flex items-center justify-center gap-1.5"
              style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}
            >
              {openFull.label} <ExternalLink size={12} />
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
